/* eslint-disable react-hooks/exhaustive-deps */
import {
  Box,
  Flex,
  Space,
  Switch,
  Text,
  useColorMode,
  useColorStyle,
} from '@tonic-one/react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { GlobalApi } from '@Apis';
import ErrorAlert from '../../../../components/Common/ErrorAlert';
import { useDisclosure } from '../../../../components/Common/Tooltip/TextEllipsis/utils';
import HeaderFieldList from '../../../../components/HeaderFieldList';
import config from '../../../../config';
import {
  ApprovedHeaderFieldsItem,
  ApprovedHeaderFieldsProps,
} from '../../../../types/globalSetting';
import { PolicyTypeEnum } from '../../../Policy/utils/constants';
import DelModal from '../../../Report/ActionModal/del';
import { safeDecodeURIComponent } from '../../../../utils/common';

const ApprovedHeaderFields = (props: {
  policyType: PolicyTypeEnum;
  withEditPermission: boolean;
  loading: boolean;
  setLoading: (loading: boolean) => void;
}) => {
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const { t } = useTranslation();
  const [errorMsg, setErrorMsg] = useState<string | undefined>(undefined);
  const [enabledStatus, setEnabledStatus] = useState<boolean>(false);
  const [items, setItems] = useState<ApprovedHeaderFieldsItem[]>();
  async function switchApprovedHeaderFieldsStatus(enabledStatus: boolean) {
    try {
      props.setLoading(true);
      const res = await GlobalApi.switchApprovedHeaderFields(
        props.policyType,
        enabledStatus
      );
      if (res.data) {
        setEnabledStatus(enabledStatus);
        const msg = enabledStatus
          ? t('globalSetting.approvedHeaderFields.switch.enabled.success')
          : t('globalSetting.approvedHeaderFields.switch.disabled.success');
        window.showNotification({
          titleMsg: msg,
          severity: 'success',
        });
      } else {
        window.showNotification({
          titleMsg: t('common.apiException.internalError'),
          severity: 'error',
        });
      }
    } catch (error) {
      window.showNotification({
        titleMsg: t('common.apiException.internalError'),
        severity: 'error',
      });
    } finally {
      props.setLoading(false);
    }
  }
  const operators = React.useMemo(() => {
    return [
      { value: 'equals', name: t('common.label.equals') },
      { value: 'contains', name: t('common.label.contains') },
    ];
  }, []);
  useEffect(() => {
    reloadData();
  }, []);
  const [delModalTitle, setDelModalTitle] = useState(
    'Delete Approved Header Fields'
  );
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [delModalContent, setDelModalContent] = useState<
    JSX.Element | undefined
  >(undefined);
  const [selectedApprovedHeadersItem, setSelectedApprovedHeadersItem] =
    useState<ApprovedHeaderFieldsItem>();
  const [delLoading, setDelLoading] = useState(false);
  const onDelete = (value: ApprovedHeaderFieldsItem) => {
    setSelectedApprovedHeadersItem(value);
    setDelModalTitle(t('globalSetting.approvedHeaderFields.delete.title'));
    setDelModalContent(
      <Text mb={8}>{`${safeDecodeURIComponent(value.key)} ${
        value.operator
      } ${safeDecodeURIComponent(value.value)}`}</Text>
    );
    onOpen();
  };

  const onAdd = async (approvedHeaderFieldsItem: ApprovedHeaderFieldsItem) => {
    console.log('add', approvedHeaderFieldsItem);
    const res = await GlobalApi.addApprovedHeaderFields(
      props.policyType,
      approvedHeaderFieldsItem
    );
    if (res.data?.createCount) {
      window.showNotification({
        titleMsg: t('globalSetting.approvedHeaderFields.add.success'),
        severity: 'success',
      });
    } else {
      window.showNotification({
        titleMsg: t('common.apiException.internalError'),
        severity: 'error',
      });
    }
    await reloadData();
  };

  function reloadData() {
    (async function doCall() {
      try {
        props.setLoading(true);
        const approvedHeaderFields: ApprovedHeaderFieldsProps = (
          await GlobalApi.getApprovedHeaderFields(props.policyType)
        ).data;
        setItems(approvedHeaderFields.data);
        setEnabledStatus(approvedHeaderFields.enableStatus);
      } catch (error) {
        setErrorMsg(t('common.apiException.internalError'));
      } finally {
        props.setLoading(false);
      }
    })();
  }

  const confirmDel = async () => {
    setDelLoading(true);
    if (!selectedApprovedHeadersItem) return;
    const res = await GlobalApi.deleteApprovedHeaderFields(props.policyType, {
      key: safeDecodeURIComponent(selectedApprovedHeadersItem.key),
      operator: selectedApprovedHeadersItem.operator,
      value: safeDecodeURIComponent(selectedApprovedHeadersItem.value),
    });
    if (res.data?.deleteCount) {
      window.showNotification({
        titleMsg: t('globalSetting.approvedHeaderFields.delete.success'),
        severity: 'success',
      });
    } else {
      window.showNotification({
        titleMsg: t('common.apiException.internalError'),
        severity: 'error',
      });
    }
    await reloadData();
    setDelLoading(false);
    onClose();
  };
  return (
    <Box>
      <Box>
        <DelModal
          visible={isOpen}
          onClose={onClose}
          onConfirm={confirmDel}
          loading={delLoading}
          title={delModalTitle}
          content={delModalContent}
          withEditPermission={props.withEditPermission}
        ></DelModal>
        {errorMsg && (
          <ErrorAlert
            errorMsg={errorMsg}
            onClose={() => setErrorMsg(undefined)}
          />
        )}
        <Flex m="16px 0px">
          <Text fontSize="lg" lineHeight="lg" color={colorStyle.color.primary}>
            {t('globalSetting.approvedHeaderFields.tab.title')}
          </Text>
        </Flex>
        <Flex m="16px 0px">
          <Text
            color={colorStyle.color.secondary}
            lineHeight="lg"
            maxWidth={1000}
          >
            {t('globalSetting.approvedHeaderFields.description', {
              service:
                props.policyType === PolicyTypeEnum.Exchange
                  ? t('common.service.exchange')
                  : t('common.service.gmail'),
              count:
                config.ui.globalSettings.limitation.approvedHeaderMaxNumber,
            })}
          </Text>
        </Flex>
        <Flex m="16px 0px">
          <Text>
            <Space>{t('globalSetting.note.title')}</Space>
            <Space width="1x"></Space>
            <Space color={colorStyle.color.secondary}>
              {t('globalSetting.note.applyToAllOrganizations')}
            </Space>
          </Text>
        </Flex>
        <Flex alignItems="center">
          <Switch
            pr="1x"
            id="approvedHeaderFields"
            checked={enabledStatus}
            onChange={async (e: any) => {
              await switchApprovedHeaderFieldsStatus(e.target.checked);
            }}
            disabled={!props.withEditPermission}
          />
          <Text>{t('globalSetting.approvedHeaderFields.switch.label')}</Text>
        </Flex>
        <Box mt={16}>
          <HeaderFieldList
            onDelete={onDelete}
            onAdd={onAdd}
            list={items || []}
            disabled={!enabledStatus || !props.withEditPermission}
            limit={config.ui.globalSettings.limitation.approvedHeaderMaxNumber}
            description={''}
            width={'800px'}
            size={5}
            operators={operators}
            headerMaxLength={
              config.ui.globalSettings.limitation.approvedHeaderMaxLength
            }
          />
        </Box>
      </Box>
    </Box>
  );
};
export default ApprovedHeaderFields;
